import { createFileRoute, useNavigate, Navigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { AdisyonBar } from "@/components/dashboard/adisyon-bar";
import { CategoryRail } from "@/components/dashboard/category-rail";
import { useCart } from "@/components/dashboard/cart-context";
import { categories, products } from "@/components/dashboard/data";
import { OrderPanel } from "@/components/dashboard/order-panel";
import { ProductGrid } from "@/components/dashboard/product-grid";
import { QuickActions } from "@/components/dashboard/quick-actions";
import { DashboardStatusBar } from "@/components/dashboard/status-bar";
import { DashboardTopBar } from "@/components/dashboard/top-bar";
import {
  ProductModifiersDialog,
  MODIFIER_GROUPS_BY_CATEGORY,
} from "@/components/dashboard/product-modifiers-dialog";
import type { Product } from "@/components/dashboard/data";

import { z } from "zod";

const dashboardSearchSchema = z.object({
  activeCategory: z.string().catch("favorites").default("favorites"),
});

export const Route = createFileRoute("/_auth/dashboard")({
  validateSearch: dashboardSearchSchema,
  component: RouteComponent,
});

function RouteComponent() {
  const { session } = Route.useRouteContext();
  const { activeCategory } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const userName = session.data?.user.name ?? "Liam Basil";

  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000 * 30);
    return () => clearInterval(interval);
  }, []);

  const [selectedModifierProduct, setSelectedModifierProduct] = useState<Product | null>(null);

  const cart = useCart();

  if (!cart.selectedTableId) {
    return <Navigate to="/tables" replace />;
  }

  const adisyonlar = cart.tableAdisyonlar[cart.selectedTableId] ?? [];
  const adisyonIndex = adisyonlar.findIndex((a) => a.id === cart.selectedAdisyon?.id);
  const adisyonLabel =
    adisyonlar.length > 1 && adisyonIndex >= 0 ? String.fromCharCode(65 + adisyonIndex) : undefined;

  const filteredProducts =
    activeCategory === "favorites"
      ? products.filter((p) => p.isFavorite)
      : products.filter((p) => p.categoryId === activeCategory);

  return (
    <div className="flex h-full flex-col overflow-hidden bg-[#f7f7f8]">
      <DashboardTopBar
        tableNumber={cart.selectedTable?.name ?? ""}
        adisyonLabel={adisyonLabel}
        notificationCount={3}
        dateLabel={now.toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
        })}
        timeLabel={now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
        userName={userName}
        userRole="Manager"
        userImage={session.data?.user.image}
      />

      <div className="flex flex-1 overflow-hidden">
        <div className="flex-1 pl-6 pr-2 py-5 flex flex-col overflow-hidden">
          <div className="flex gap-4 h-full min-h-0 relative">
            <CategoryRail
              activeCategory={activeCategory}
              onSelectCategory={(cat) => navigate({ search: { activeCategory: cat } })}
              onBack={() => navigate({ to: "/tables" })}
            />

            <div className="flex flex-1 flex-col gap-4 h-full min-h-0">
              <ProductGrid
                title={categories.find((c) => c.id === activeCategory)?.label ?? "Products"}
                products={filteredProducts}
                onAddProduct={(product) => {
                  cart.addProduct(product);
                }}
                onConfigureProduct={(product) => {
                  setSelectedModifierProduct(product);
                }}
                className="flex-1"
              />
              <QuickActions />
            </div>
          </div>
        </div>

        <div className="w-[380px] shrink-0 py-5 pl-2 pr-6">
          <div className="flex h-full flex-col rounded-2xl border border-gray-200 bg-white p-4">
            <AdisyonBar />
            <div className="min-h-0 flex-1">
              <OrderPanel
                items={cart.orderItems}
                subtotal={cart.subtotal}
                discount={cart.discount}
                tax={cart.tax}
                total={cart.total}
                onIncrement={cart.increment}
                onDecrement={cart.decrement}
                onRemove={cart.remove}
                onClear={cart.clear}
                onPay={() => navigate({ to: "/payment" })}
              />
            </div>
          </div>
        </div>
      </div>

      <DashboardStatusBar />

      {selectedModifierProduct && (
        <ProductModifiersDialog
          product={selectedModifierProduct}
          onClose={() => setSelectedModifierProduct(null)}
          onConfirm={(customProduct) => {
            cart.addProduct(customProduct);
            setSelectedModifierProduct(null);
          }}
        />
      )}
    </div>
  );
}
